/**
 * React hooks over the runtime feature toggles (see ./featureToggles.ts for the
 * pure resolution core and the setting-key contract).
 *
 * The read hook fetches the four `system_settings` rows once and caches them;
 * `useFeatureToggle` and `useResolvedLightCategoryFlags` resolve `runtime ??
 * envDefault` on top. `useSetFeatureToggle` is the super-admin write.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { LightCategoryFlags } from '@/features/mockups/utils/mockupLightCategories';
import {
  FEATURE_TOGGLE_SETTING_KEYS,
  FEATURE_TOGGLE_SETTING_KEY_LIST,
  parseFeatureToggleRows,
  resolveFeatureToggle,
  resolveLightCategoryFlags,
  type FeatureToggleName,
  type FeatureToggleRuntimeMap,
} from './featureToggles';

/** Shared query key so a super-admin write can invalidate every reader. */
export const FEATURE_TOGGLES_QUERY_KEY = ['system-settings', 'feature_toggles'] as const;

/** Maximum delay before an open customer session observes an admin change. */
export const FEATURE_TOGGLE_REFRESH_MS = 15_000;

/**
 * Reads the four runtime feature-toggle rows from system_settings. The rows are
 * readable by any authenticated user via a narrow RLS policy (the rest of
 * system_settings is super-admin only). Returns a map of PRESENT rows only —
 * absent keys fall through to the env default at resolve time.
 */
export function useFeatureToggleSettings() {
  return useQuery({
    queryKey: FEATURE_TOGGLES_QUERY_KEY,
    queryFn: async (): Promise<FeatureToggleRuntimeMap> => {
      const { data, error } = await supabase
        .from('system_settings')
        .select('setting_key, setting_value')
        .in('setting_key', FEATURE_TOGGLE_SETTING_KEY_LIST);
      if (error) throw error;
      return parseFeatureToggleRows(data);
    },
    staleTime: 0,
    refetchInterval: FEATURE_TOGGLE_REFRESH_MS,
    refetchOnWindowFocus: true,
  });
}

/**
 * The resolved boolean for one feature: the runtime toggle when present,
 * otherwise the build-time env-flag default. While the query is loading or if it
 * errors `data` is undefined, so the env default is returned and a gated feature
 * never flickers off before the toggle has loaded.
 */
export function useFeatureToggle(name: FeatureToggleName): boolean {
  const { data } = useFeatureToggleSettings();
  return resolveFeatureToggle(name, data ?? {});
}

/**
 * The three light categories resolved into a `LIGHT_CATEGORY_FLAGS`-shaped
 * object, to hand to the mockup editor's toolbar + creation boundary. Renderers
 * never receive this — saved runs always render (see layoutFlags.test.ts).
 */
export function useResolvedLightCategoryFlags(): LightCategoryFlags {
  const { data } = useFeatureToggleSettings();
  return resolveLightCategoryFlags(data ?? {});
}

/**
 * Super-admin write: upsert one feature toggle row. Mirrors the mobile_app_live
 * upsert (`onConflict: 'setting_key'`); writes are gated to super-admins by the
 * existing system_settings INSERT/UPDATE policies. Invalidates the shared query
 * so every reader picks the new value up immediately.
 */
export function useSetFeatureToggle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      name,
      enabled,
    }: {
      name: FeatureToggleName;
      enabled: boolean;
    }) => {
      const { error } = await supabase
        .from('system_settings')
        .upsert(
          {
            setting_key: FEATURE_TOGGLE_SETTING_KEYS[name],
            setting_value: { enabled },
          },
          { onConflict: 'setting_key' },
        );
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FEATURE_TOGGLES_QUERY_KEY });
    },
  });
}
